import React, { useState, useRef, useEffect } from 'react';

export default function ServicesSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const [slideStep, setSlideStep] = useState(0);

  const services = [
    {
      icon: 'ri-home-heart-line',
      title: 'Wohnungsreinigung',
      description:
        'Sorgfältige Unterstützung bei der Wohnungsreinigung für ein sauberes, sicheres und angenehmes Zuhause.',
      image: '/images/services/5.png'
    },
    {
      icon: 'ri-shirt-line',
      title: 'Wäschepflege',
      description:
        'Unterstützung beim Waschen, Sortieren und Organisieren der Wäsche im Alltag.',
      image: '/images/services/6.png'
    },
    {
      icon: 'ri-window-line',
      title: 'Fensterreinigung',
      description:
        'Hilfe bei der regelmäßigen Fensterreinigung für mehr Licht und Ordnung in der Wohnung.',
      image: '/images/services/7.png'
    },
    {
      icon: 'ri-shopping-basket-2-line',
      title: 'Einkaufen',
      description:
        'Begleitung oder Übernahme von Einkäufen und Besorgungen des täglichen Bedarfs.',
      image: '/images/services/8.png'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Termine begleiten',
      description:
        'Begleitung zu Arztterminen, Behördengängen oder anderen wichtigen Wegen außer Haus.',
      image: '/images/services/9.png'
    },
    {
      icon: 'ri-service-line',
      title: 'Weitere Alltagshilfe',
      description:
        'Botengänge, Kochen, Mobilitätshilfe, Gesellschaft leisten und Alltagsorganisation werden individuell abgestimmt.',
      image: '/images/services/10.png'
    }
  ];

  const goToSlide = (index: number) => {
    const normalizedIndex = (index + services.length) % services.length;
    if (normalizedIndex === currentIndex) return;

    setCurrentIndex(normalizedIndex);

    const el = mobileScrollRef.current;
    if (el && slideStep > 0) {
      el.scrollTo({
        left: normalizedIndex * slideStep,
        behavior: 'smooth',
      });
    }
  };

  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    goToSlide(currentIndex + 1);
    setTimeout(() => setIsTransitioning(false), 350);
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    goToSlide(currentIndex - 1);
    setTimeout(() => setIsTransitioning(false), 350);
  };

  useEffect(() => {
    const el = mobileScrollRef.current;
    if (!el) return;

    const compute = () => {
      const first = el.querySelector<HTMLElement>('[data-slide]');
      if (!first) return;

      const gap = 24;
      setSlideStep(first.offsetWidth + gap);
    };

    compute();

    const ro = new ResizeObserver(() => compute());
    ro.observe(el);

    return () => {
      ro.disconnect();
    };
  }, []);

  const handleMobileScroll = () => {
    const el = mobileScrollRef.current;
    if (!el || slideStep <= 0) return;

    const newIndex = Math.round(el.scrollLeft / slideStep);
    if (newIndex !== currentIndex) setCurrentIndex(newIndex);
  };

  const handleAnchorClick = (targetId: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    const isHome = window.location.pathname === '/';
    const target = document.querySelector(targetId);

    if (isHome && target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.pushState(null, '', targetId);
      return;
    }

    window.location.href = `/${targetId}`;
  };

  return (
    <section
      id="services"
      className="relative overflow-hidden py-24 md:py-28 bg-[linear-gradient(180deg,#F8FBFF_0%,#EAF6FF_100%)] text-[#0F172A] scroll-mt-28 md:scroll-mt-32"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[-7rem] top-10 h-72 w-72 rounded-full bg-[#027FDB]/18 blur-3xl" />
        <div className="absolute right-[-8rem] top-1/3 h-80 w-80 rounded-full bg-white/60 blur-3xl" />
        <div className="absolute bottom-[-6rem] left-1/3 h-72 w-72 rounded-full bg-[#027FDB]/12 blur-3xl" />
      </div>

      <div className="relative z-10 container mx-auto px-4">
        <div className="max-w-6xl mx-auto mb-14 lg:mb-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-2xl">
              <p className="text-[11px] md:text-xs tracking-[0.18em] uppercase mb-3 text-[#027FDB] font-semibold">
                Leistungen · Alltagshilfe in Berlin
              </p>
              <h2 className="text-3xl lg:text-4xl xl:text-[2.8rem] font-semibold mb-3 lg:mb-0 text-[#0F172A] leading-tight">
                Unsere Leistungen für mehr Entlastung, Struktur und Unterstützung im Alltag
              </h2>
            </div>
            <p className="text-sm lg:text-base max-w-xl lg:text-right text-[#475569] leading-relaxed lg:pl-8">
              Alltagsstern UG unterstützt Personen mit Pflegegrad in Berlin mit verlässlicher Alltagshilfe nach §45b SGB XI, von Haushaltsaufgaben bis zur Begleitung im Alltag.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2 lg:justify-end">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#027FDB]/10 bg-white/80 px-3 py-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-[#027FDB] shadow-[0_8px_24px_rgba(15,23,42,0.05)] backdrop-blur-sm">
                <i className="ri-map-pin-line text-sm" />
                Berlinweit
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-[#027FDB]/10 bg-white/80 px-3 py-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-[#027FDB] shadow-[0_8px_24px_rgba(15,23,42,0.05)] backdrop-blur-sm">
                <i className="ri-shield-check-line text-sm" />
                §45b SGB XI
              </span>
            </div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="w-full px-0 lg:px-4">
            <div
              ref={mobileScrollRef}
              className="w-full flex overflow-x-auto snap-x snap-mandatory gap-6 px-2 sm:px-3 py-3 no-scrollbar select-none"
              onScroll={handleMobileScroll}
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {services.map((svc) => {
                return (
                <div key={svc.title} data-slide className="shrink-0 w-[88%] sm:w-[78%] md:w-[44%] lg:w-[34%] snap-center">
                  <div
                    className="relative overflow-hidden rounded-[2rem] border border-blue-100 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.08)] transition-all duration-300 h-[500px] sm:h-[490px] md:h-[480px] lg:h-[500px] flex flex-col md:hover:-translate-y-1 md:hover:shadow-[0_28px_80px_rgba(29,78,216,0.10)]"
                  >
                    <span
                      className="absolute right-4 top-4 z-10 inline-flex items-center gap-1 rounded-full border border-blue-100 bg-white/95 px-3 py-1 text-[0.65rem] font-semibold tracking-[0.14em] uppercase text-[#027FDB] shadow-[0_8px_20px_rgba(2,127,219,0.08)]"
                    >
                      <i className="ri-shield-check-line text-sm" />
                      Alltagshilfe
                    </span>
                    <>
                        <div className="relative h-[48%] overflow-hidden bg-[#EAF6FF]">
                          <img
                            src={svc.image}
                            alt={svc.title}
                            className="w-full h-full object-cover object-center transition-transform duration-500 md:group-hover:scale-[1.02]"
                            draggable={false}
                            loading="lazy"
                            decoding="async"
                          />
                        </div>

                        <div className="flex h-[52%] flex-col justify-between px-6 pt-5 pb-6">
                          <div>
                            <div className="flex items-center gap-3">
                              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-blue-100 bg-[#EAF6FF] shadow-[0_8px_20px_rgba(2,127,219,0.08)]">
                                <i className={`${svc.icon} text-[1.35rem] text-[#027FDB]`} />
                              </span>
                              <h3 className="text-base md:text-lg font-semibold text-[#0F172A] leading-snug">
                                {svc.title}
                              </h3>
                            </div>
                            <p className="mt-4 leading-relaxed text-sm md:text-base text-[#475569]">
                              {svc.description}
                            </p>
                          </div>

                        </div>
                      </>
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col items-center justify-center mt-8 lg:mt-10 gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={prevSlide}
                disabled={isTransitioning}
                className="w-11 h-11 flex items-center justify-center rounded-full border border-blue-100 bg-white hover:bg-[#EAF6FF] transition-all duration-200 cursor-pointer disabled:opacity-40 shadow-[0_10px_28px_rgba(15,23,42,0.08)]"
              >
                <i className="ri-arrow-left-s-line text-lg text-[#027FDB]" />
              </button>

              <div className="flex items-center gap-2 rounded-full border border-blue-100 bg-white/90 px-3 py-2 shadow-[0_8px_24px_rgba(15,23,42,0.06)]">
                {services.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    disabled={isTransitioning}
                    className={`h-2.5 rounded-full bg-[#027FDB] transition-all duration-300 cursor-pointer disabled:opacity-50 ${
                      currentIndex === index ? 'w-7 opacity-100' : 'w-2.5 opacity-30'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={nextSlide}
                disabled={isTransitioning}
                className="w-11 h-11 flex items-center justify-center rounded-full border border-blue-100 bg-white hover:bg-[#EAF6FF] transition-all duration-200 cursor-pointer disabled:opacity-40 shadow-[0_10px_28px_rgba(15,23,42,0.08)]"
              >
                <i className="ri-arrow-right-s-line text-lg text-[#027FDB]" />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-16 rounded-[2rem] border border-[#027FDB]/10 bg-[linear-gradient(135deg,#026CB8_0%,#027FDB_100%)] px-6 py-8 sm:px-8 lg:px-10 lg:py-10 shadow-[0_24px_80px_rgba(2,127,219,0.18)]">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl text-white">
              <p className="text-[0.7rem] sm:text-[0.75rem] font-semibold uppercase tracking-[0.18em] text-white/70">Unverbindliche Erstberatung</p>
              <h3 className="mt-3 text-2xl sm:text-3xl lg:text-[2.2rem] font-semibold leading-tight">
                Gemeinsam klären, welche Unterstützung im Alltag wirklich sinnvoll ist
              </h3>
              <p className="mt-3 text-sm sm:text-base leading-relaxed text-white/85">
                Persönlich, ruhig und verständlich. Wir besprechen Ihren Bedarf in Berlin und zeigen, welche Alltagshilfe nach §45b SGB XI sinnvoll passt.
              </p>
            </div>

            <div className="flex flex-col gap-3 lg:flex-shrink-0 lg:items-end lg:justify-center">
              <a
                href="#kontakt"
                onClick={handleAnchorClick('#kontakt')}
                className="inline-flex min-h-[3.5rem] items-center justify-center gap-2 rounded-2xl bg-white px-6 py-4 text-[0.8rem] font-semibold uppercase tracking-[0.14em] text-[#027FDB] shadow-[0_12px_30px_rgba(15,23,42,0.12)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#EAF6FF]"
              >
                <i className="ri-mail-send-line text-xl" />
                Unverbindliche Erstberatung anfragen
              </a>

              <a
                href="tel:+4915229990328"
                className="inline-flex min-h-[3.5rem] items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-6 py-4 text-[0.8rem] font-semibold uppercase tracking-[0.14em] text-white backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/15"
              >
                <i className="ri-phone-line text-xl" />
                Direkt anrufen
              </a>

              <a
                href="mailto:info@alltagsstern.com"
                className="inline-flex min-h-[3.5rem] items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-6 py-4 text-[0.8rem] font-semibold tracking-[0.04em] text-white backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/15"
              >
                <i className="ri-mail-line text-xl" />
                info@alltagsstern.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
